// ProgressService.js - Real progress tracking service
import AuthService from './AuthService';

const API_BASE_URL = '/api';

class ProgressService {
  constructor() {
    this.progressCache = null;
    this.lastSync = null;
  }

  // Get user's complete progress
  async getUserProgress() {
    try {
      if (!AuthService.isAuthenticated()) {
        return { success: false, message: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE_URL}/progress`, {
        headers: AuthService.getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          this.progressCache = result.data;
          this.lastSync = new Date();
          
          // Also update localStorage for offline access
          localStorage.setItem('user_progress', JSON.stringify(result.data));
          localStorage.setItem('progress_last_sync', this.lastSync.toISOString());
          
          return { success: true, data: result.data };
        } else {
          return { success: false, message: result.message };
        }
      } else {
        return { success: false, message: 'Failed to fetch progress' };
      }
    } catch (error) {
      console.error('Get progress error:', error);
      
      // Try to load from localStorage as fallback
      const cachedProgress = localStorage.getItem('user_progress');
      if (cachedProgress) {
        try {
          const data = JSON.parse(cachedProgress);
          return { success: true, data, fromCache: true };
        } catch (parseError) {
          console.error('Failed to parse cached progress:', parseError);
        }
      }
      
      return { success: false, message: 'Network error and no cached data available' };
    }
  }

  // Save general progress
  async saveProgress(progressType, lessonId = null, cardId = null, exerciseId = null, progressData = null) {
    try {
      if (!AuthService.isAuthenticated()) {
        return { success: false, message: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE_URL}/progress`, {
        method: 'POST',
        headers: AuthService.getAuthHeaders(),
        body: JSON.stringify({
          lessonId,
          cardId,
          exerciseId,
          progressType,
          progressData
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          // Refresh progress cache
          await this.getUserProgress();
        }
        
        return result;
      } else {
        return { success: false, message: 'Failed to save progress' };
      }
    } catch (error) {
      console.error('Save progress error:', error);
      
      // Queue for later sync if offline
      this.queueProgressForSync(progressType, lessonId, cardId, exerciseId, progressData);
      
      return { success: false, message: 'Network error - queued for later sync' };
    }
  }

  // Start a lesson
  async startLesson(lessonId) {
    try {
      if (!AuthService.isAuthenticated()) {
        return { success: false, message: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE_URL}/progress/lesson/${lessonId}/start`, {
        method: 'POST',
        headers: AuthService.getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          await this.getUserProgress(); // Refresh cache
        }
        
        return result;
      } else {
        return { success: false, message: 'Failed to start lesson' };
      }
    } catch (error) {
      console.error('Start lesson error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  }

  // Complete a lesson
  async completeLesson(lessonId) {
    try {
      if (!AuthService.isAuthenticated()) {
        return { success: false, message: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE_URL}/progress/lesson/${lessonId}/complete`, {
        method: 'POST',
        headers: AuthService.getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          await this.getUserProgress(); // Refresh cache
        }
        
        return result;
      } else {
        return { success: false, message: 'Failed to complete lesson' };
      }
    } catch (error) {
      console.error('Complete lesson error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  }

  // Complete an exercise
  async completeExercise(cardId, exerciseData = null) {
    try {
      if (!AuthService.isAuthenticated()) {
        return { success: false, message: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE_URL}/progress/exercise/${cardId}/complete`, {
        method: 'POST',
        headers: AuthService.getAuthHeaders(),
        body: JSON.stringify(exerciseData || {})
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          await this.getUserProgress(); // Refresh cache
        }
        
        return result;
      } else {
        return { success: false, message: 'Failed to complete exercise' };
      }
    } catch (error) {
      console.error('Complete exercise error:', error);
      
      // Queue for later sync if offline
      this.queueProgressForSync('exercise_completed', null, cardId, null, exerciseData);
      
      return { success: false, message: 'Network error - queued for later sync' };
    }
  }

  // Reset all progress
  async resetProgress() {
    try {
      if (!AuthService.isAuthenticated()) {
        return { success: false, message: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE_URL}/progress/reset`, {
        method: 'POST',
        headers: AuthService.getAuthHeaders()
    });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          this.progressCache = null;
          localStorage.removeItem('user_progress');
          localStorage.removeItem('progress_last_sync');
        }
        
        return result;
      } else {
        return { success: false, message: 'Failed to reset progress' };
      }
    } catch (error) {
      console.error('Reset progress error:', error);
      return { success: false, message: 'Network error occurred' };
    }
  }

  // Check if lesson is completed
  isLessonCompleted(lessonId) {
    if (!this.progressCache || !this.progressCache.lessons) {
      const cached = localStorage.getItem('user_progress');
      if (cached) {
        try {
          this.progressCache = JSON.parse(cached);
        } catch (error) {
          return false;
        }
      } else {
        return false;
      }
    }

    const lesson = this.progressCache.lessons[lessonId];
    return lesson ? lesson.isCompleted : false;
  }

  // Check if exercise is completed
  isExerciseCompleted(cardId) {
    if (!this.progressCache || !this.progressCache.completedExercises) {
      const cached = localStorage.getItem('user_progress');
      if (cached) {
        try {
          this.progressCache = JSON.parse(cached);
        } catch (error) {
          return false;
        }
      } else {
        return false;
      }
    }

    return this.progressCache.completedExercises[cardId] || false;
  }

  // Get lesson status
  getLessonStatus(lessonId) {
    if (!this.progressCache || !this.progressCache.lessons) {
      const cached = localStorage.getItem('user_progress');
      if (cached) {
        try {
          this.progressCache = JSON.parse(cached);
        } catch (error) {
          return 'remaining';
        }
      } else {
        return 'remaining';
      }
    }

    const lesson = this.progressCache.lessons[lessonId];
    if (!lesson) return 'remaining';
    
    if (lesson.isCompleted) return 'completed';
    if (lesson.isStarted) return 'in-progress';
    return 'remaining';
  }

  // Get progress statistics
  getProgressStats() {
    if (!this.progressCache) {
      const cached = localStorage.getItem('user_progress');
      if (cached) {
        try {
          this.progressCache = JSON.parse(cached);
        } catch (error) {
          return { totalLessons: 0, completedLessons: 0, totalExercises: 0, completedExercises: 0 };
        }
      } else {
        return { totalLessons: 0, completedLessons: 0, totalExercises: 0, completedExercises: 0 };
      }
    }

    return {
      totalLessons: this.progressCache.totalLessons || 0,
      completedLessons: this.progressCache.completedLessons || 0,
      totalExercises: this.progressCache.totalExercises || 0,
      completedExercises: Object.keys(this.progressCache.completedExercises || {}).length
    };
  }

  // Queue progress for later sync when offline
  queueProgressForSync(progressType, lessonId, cardId, exerciseId, progressData) {
    const queue = JSON.parse(localStorage.getItem('progress_sync_queue') || '[]');
    
    queue.push({
      progressType,
      lessonId,
      cardId,
      exerciseId,
      progressData,
      timestamp: new Date().toISOString()
    });
    
    localStorage.setItem('progress_sync_queue', JSON.stringify(queue));
  }

  // Sync queued progress when back online
  async syncQueuedProgress() {
    try {
      const queue = JSON.parse(localStorage.getItem('progress_sync_queue') || '[]');
      
      if (queue.length === 0) return { success: true, synced: 0 };

      let syncedCount = 0;
      const failedItems = [];

      for (const item of queue) {
        const result = await this.saveProgress(
          item.progressType,
          item.lessonId,
          item.cardId,
          item.exerciseId,
          item.progressData
        );

        if (result.success) {
          syncedCount++;
        } else {
          failedItems.push(item);
        }
      }

      // Update queue with failed items only
      localStorage.setItem('progress_sync_queue', JSON.stringify(failedItems));

      return { success: true, synced: syncedCount, failed: failedItems.length };
    } catch (error) {
      console.error('Sync queued progress error:', error);
      return { success: false, message: 'Failed to sync queued progress' };
    }
  }

  // Clear cache (useful for debugging or when switching users)
  clearCache() {
    this.progressCache = null;
    this.lastSync = null;
    localStorage.removeItem('user_progress');
    localStorage.removeItem('progress_last_sync');
    localStorage.removeItem('progress_sync_queue');
  }
}

export default new ProgressService();